import { Button } from '@features/ui';
import { BillingForm } from '@src/features/user/checkout';
import { paymentFormSchema, PaymentFormValues } from '@src/types/packages';
import { MarketDataItem } from '@src/types/marketData';
import { useAuthStore } from '@stores/authStore';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';

export const MarketDataCheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { auth } = useAuthStore();

  const selectedMarket = (location.state?.selectedMarket ||
    null) as MarketDataItem | null;

  useEffect(() => {
    if (!selectedMarket) {
      navigate('/market-data');
    }
  }, [selectedMarket, navigate]);

  const defaultValues: Partial<PaymentFormValues> = useMemo(
    () => ({
      payment_type: 'stripe',
      first_name: auth.user?.first_name ?? '',
      last_name: auth.user?.last_name ?? '',
      street: '',
      city: auth.user?.city ?? '',
      state: auth.user?.state ?? '',
      zip_code: auth.user?.zipcode ?? '',
      country: auth.user?.country ?? 'US',
      phone_number: auth.user?.phone_number ?? '',
      coupon_code: '',
      referral_source: '',
    }),
    [auth.user]
  );

  const form = useForm<PaymentFormValues>({
    resolver: yupResolver(paymentFormSchema),
    defaultValues,
  });

  const { handleSubmit, reset } = form;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const onSubmit = useCallback(
    async (values: PaymentFormValues) => {
      try {
        const res = await axios.post('/market-data-access/checkout/', {
          market_data_id: selectedMarket?.id,
          leeloo_user_id: auth.user?.id || null,
          payment_gateway: values.payment_type,
          coupon_code: values.coupon_code || undefined,
          billing_info: {
            first_name: values.first_name,
            last_name: values.last_name,
            street: values.street,
            city: values.city,
            state: values.state,
            zip_code: values.zip_code,
            country: values.country,
            phone_number: values.phone_number,
          },
        });

        window.location.href = res.data.url;
      } catch (error: any) {
        console.error('Checkout error:', error);
        toast.error(
          error?.response?.data?.message ||
            'Failed to process checkout. Please try again.'
        );
      }
    },
    [selectedMarket, navigate]
  );

  if (!selectedMarket) {
    return null;
  }

  return (
    <div className="min-h-screen pb-[120px]">
      <div className="max-w-[1440px] m-auto px-6 lg:px-[60px] mt-8 py-8">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="bg-[#1F2734] rounded-[10px] flex flex-col lg:flex-row gap-6 p-6">
            {/* Left Side - Order Summary */}
            <div className="flex-1 space-y-4">
              <div>
                <h2 className="font-bold text-[28px] leading-[100%] tracking-normal text-white mb-2">
                  Market Data Checkout
                </h2>
                <p className="pt-[10px] font-medium text-[16px] leading-[18px] tracking-normal text-white/70 max-w-[617px]">
                  Review your selected market data and complete your purchase
                </p>
              </div>

              <div className="rounded-[10px] p-[20px] flex flex-col gap-[30px]">
                <div className="flex flex-col items-start justify-center p-[30px] pt-5 bg-[#253348] rounded-[10px] border-[#33455F] border">
                  <p className="text-[18px] font-bold text-white">
                    Order Summary
                  </p>

                  {/* Market Data Items */}
                  <div className="flex items-start justify-between w-full gap-[15px]">
                    <div className="space-y-[10px] w-full">
                      <p className="text-sm text-[#818FA4] font-normal mt-[30px]">
                        Market Data Subscriptions
                      </p>
                      <div className="space-y-3 ps-[10px]">
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-white font-semibold text-[14px]">
                            {selectedMarket.display_name_web}
                          </p>
                          <p className="text-white font-semibold text-[14px] whitespace-nowrap">
                            $
                            {selectedMarket.billing_plan.first_price.toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Total Divider */}
                  <div className="border-b-[3px] w-full border-[#33455F] pt-[30px] mb-[30px]" />

                  {/* Total Due Today */}
                  <div className="flex items-center justify-between w-full mt-5 gap-[6px]">
                    <p className="text-white font-bold text-[22px]">Total</p>
                    <p className="text-white font-normal text-[22px]">
                      ${selectedMarket.billing_plan.first_price.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Billing Information */}
            <div className="flex flex-1 gap-10">
              <BillingForm
                form={form}
                showSubmitButton={true}
                submitButtonText="Complete Purchase"
                submitting={false}
              />
            </div>
          </div>

          {/* Back Button */}
          <div className="mt-6">
            <Button
              className="px-8"
              size="lg"
              type="button"
              variant="outline"
              onClick={() => navigate('/market-data')}
            >
              Back to Market Data
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
